import React, { createContext, useContext, useMemo, useState } from "react";

type RosterEntry = {
  id: string;
  name: string;
};

type RosterFile = {
  classCode: string;
  exportedOn: number;
  students: RosterEntry[];
};

type ClassRosterState = {
  names: Record<string, string>;
  lastMutation: number | null;
  lastSaved: number | null;
};

type RosterState = {
  classes: Record<string, ClassRosterState>;
};

const STORAGE_KEY = "finlitri-roster-data";

const defaultState: RosterState = { classes: {} };

const loadState = (): RosterState => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return defaultState;
  }
  try {
    const parsed = JSON.parse(raw) as RosterState;
    return parsed?.classes ? parsed : defaultState;
  } catch {
    return defaultState;
  }
};

const persistState = (state: RosterState) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

const ensureClassState = (state: RosterState, classCode: string): ClassRosterState => {
  return state.classes[classCode] || { names: {}, lastMutation: null, lastSaved: null };
};

type RosterContextValue = {
  getName: (classCode: string, studentId: string) => string | undefined;
  setName: (classCode: string, studentId: string, name: string) => void;
  setNamesBatch: (classCode: string, entries: Record<string, string>) => void;
  removeName: (classCode: string, studentId: string) => void;
  getRoster: (classCode: string) => Record<string, string>;
  hasSavedSinceMutation: (classCode: string) => boolean;
  exportRoster: (classCode: string) => string;
  importRoster: (classCode: string, jsonText: string) => { imported: number };
  markSaved: (classCode: string) => void;
};

const RosterContext = createContext<RosterContextValue>({
  getName: () => undefined,
  setName: () => undefined,
  setNamesBatch: () => undefined,
  removeName: () => undefined,
  getRoster: () => ({}),
  hasSavedSinceMutation: () => true,
  exportRoster: () => "",
  importRoster: () => ({ imported: 0 }),
  markSaved: () => undefined,
});

export const useRoster = () => useContext(RosterContext);

export const RosterProvider: React.FC = ({ children }) => {
  const [state, setState] = useState<RosterState>(() => loadState());

  const commitState = (updater: (prev: RosterState) => RosterState) => {
    setState((prev) => {
      const nextState = updater(prev);
      persistState(nextState);
      return nextState;
    });
  };

  const getName = (classCode: string, studentId: string) => {
    return ensureClassState(state, classCode).names[studentId];
  };

  const setName = (classCode: string, studentId: string, name: string) => {
    commitState((prev) => {
      const classState = ensureClassState(prev, classCode);
      return {
        classes: {
          ...prev.classes,
          [classCode]: {
            ...classState,
            names: {
              ...classState.names,
              [studentId]: name,
            },
            lastMutation: Date.now(),
          },
        },
      };
    });
  };

  const setNamesBatch = (classCode: string, entries: Record<string, string>) => {
    const entryKeys = Object.keys(entries);
    if (entryKeys.length === 0) {
      return;
    }
    commitState((prev) => {
      const classState = ensureClassState(prev, classCode);
      return {
        classes: {
          ...prev.classes,
          [classCode]: {
            ...classState,
            names: {
              ...classState.names,
              ...entries,
            },
            lastMutation: Date.now(),
          },
        },
      };
    });
  };

  const removeName = (classCode: string, studentId: string) => {
    commitState((prev) => {
      const classState = ensureClassState(prev, classCode);
      const nextNames = { ...classState.names };
      delete nextNames[studentId];
      return {
        classes: {
          ...prev.classes,
          [classCode]: {
            ...classState,
            names: nextNames,
            lastMutation: Date.now(),
          },
        },
      };
    });
  };

  const getRoster = (classCode: string) => {
    return ensureClassState(state, classCode).names;
  };

  const hasSavedSinceMutation = (classCode: string) => {
    const classState = ensureClassState(state, classCode);
    if (!classState.lastMutation) {
      return true;
    }
    return (classState.lastSaved || 0) >= classState.lastMutation;
  };

  const markSaved = (classCode: string) => {
    commitState((prev) => {
      const classState = ensureClassState(prev, classCode);
      return {
        classes: {
          ...prev.classes,
          [classCode]: {
            ...classState,
            lastSaved: Date.now(),
          },
        },
      };
    });
  };

  const exportRoster = (classCode: string) => {
    const roster = ensureClassState(state, classCode);
    const file: RosterFile = {
      classCode,
      exportedOn: Date.now(),
      students: Object.entries(roster.names).map(([id, name]) => ({ id, name })),
    };
    markSaved(classCode);
    return JSON.stringify(file, null, 2);
  };

  const importRoster = (classCode: string, jsonText: string) => {
    let payload: RosterFile;
    try {
      payload = JSON.parse(jsonText) as RosterFile;
    } catch (error) {
      throw new Error("Invalid JSON file.");
    }
    if (!payload?.classCode || payload.classCode !== classCode) {
      throw new Error("Roster file does not match this class.");
    }
    if (!Array.isArray(payload.students)) {
      throw new Error("Roster file is missing students.");
    }
    const nextNames: Record<string, string> = {};
    payload.students.forEach((student) => {
      if (!student?.id || typeof student.name !== "string") {
        throw new Error("Roster file has invalid student data.");
      }
      nextNames[student.id] = student.name;
    });
    commitState((prev) => {
      const classState = ensureClassState(prev, classCode);
      return {
        classes: {
          ...prev.classes,
          [classCode]: {
            ...classState,
            names: nextNames,
            lastMutation: Date.now(),
          },
        },
      };
    });
    return { imported: payload.students.length };
  };

  const value = useMemo(() => ({
    getName,
    setName,
    setNamesBatch,
    removeName,
    getRoster,
    hasSavedSinceMutation,
    exportRoster,
    importRoster,
    markSaved,
  }), [state]);

  return (
    <RosterContext.Provider value={value}>
      {children}
    </RosterContext.Provider>
  );
};

export default RosterProvider;
